/* ------------------------------------------------------------------
 * MySanctuaryPage — auth-gated shell that exposes the two existing
 * personal features (Chapters Within + Book Whispers) at their
 * canonical route.
 *
 * Reuses (zero duplication):
 *   • ChaptersWithinSection  — Capture-a-Chapter CTA + full modal
 *   • MyStoryPage            — personal chapter library (list, detail, delete, freemium usage)
 *   • BookWhispersSection    — Whisper-a-Book CTA + full modal
 *   • All existing CSS, i18n, endpoints and email templates
 *
 * PREVIEW-ONLY Founder Review Mode:
 *   When the founder opens /my-sanctuary on a non-production host, we
 *   transparently sign in as the pre-seeded review test user so the
 *   entire UX can be inspected without hitting the auth wall.
 *   Production hostnames (icraftz.com) are strictly unchanged.
 *
 * /my-story remains routed as a backward-compatible alias.
 * ---------------------------------------------------------------- */
import { Link } from 'react-router-dom';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ChaptersWithinSection from '../components/ChaptersWithinSection';
import BookWhispersSection from '../components/BookWhispersSection';
import ParchmentBackground from '../components/ParchmentBackground';
import MyStoryPage from './MyStoryPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MySanctuaryPage() {
  const { isAuthenticated, loading, user } = useAuth();
  const navigate = useNavigate();
  const activeLang = (typeof window !== 'undefined' && localStorage.getItem('icraftz_lang')) || 'en';

  // Auth gate — unauthenticated visitors are sent to sign-in.
  React.useEffect(() => {
    if (loading || isAuthenticated) return;
    navigate('/login', { replace: true, state: { from: '/my-sanctuary' } });
  }, [loading, isAuthenticated, navigate]);

  if (loading || !isAuthenticated) {
    return (
      <div
        data-testid="sanctuary-loading"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#1a0e07',
          color: '#c19a4b',
          fontFamily: "'Cinzel', serif",
          letterSpacing: '.18em',
          fontSize: '.78rem',
          textTransform: 'uppercase',
          gap: 10,
        }}
      >
        <span>Opening your sanctuary…</span>
      </div>
    );
  }

  return (
    <div data-testid="my-sanctuary-page" style={{ minHeight: '100vh', background: 'transparent' }}>
      <ParchmentBackground />
      {/* Temporary brass strip — makes the shell recognisable without
          committing to the final v2 parchment visual system. */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 40,
          padding: '12px 20px 12px 64px',
          background: 'linear-gradient(180deg, rgba(26,14,7,.92) 0%, rgba(26,14,7,.68) 100%)',
          backdropFilter: 'blur(6px)',
          borderBottom: '1px solid rgba(163,126,42,.35)',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
        }}
      >
        <button
          onClick={() => navigate('/')}
          data-testid="sanctuary-back-home"
          aria-label="Back to Home"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 16px',
            color: '#e9d9b3',
            background: 'transparent',
            border: '1px solid rgba(163,126,42,.5)',
            borderRadius: 4,
            fontFamily: "'Cinzel', serif",
            fontSize: '.74rem',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={14} /> Home
        </button>
        <span
          style={{
            color: '#c19a4b',
            fontFamily: "'Cinzel', serif",
            fontSize: '.74rem',
            letterSpacing: '.18em',
            textTransform: 'uppercase',
          }}
        >
          My Sanctuary {user?.name ? `· ${user.name}` : ''}
        </span>
      </div>

      {/* ── SECTION 1 — CHAPTERS WITHIN ────────────────────────────── */}
      {/* The existing landing card + full "Your Chapter" modal, verbatim. */}
      <ChaptersWithinSection activeLang={activeLang} backendUrl={BACKEND_URL} hideSanctuaryNote />

      {/* The existing personal chapter library (list, detail, delete, freemium usage). */}
      {/* Rendered as-is so every previously built interaction keeps working. */}
      <div data-testid="sanctuary-my-story-embed">
        <MyStoryPage embedded />
      </div>

      <p style={{ textAlign: 'center', margin: '8px 0 0' }}>
        <Link to="/my-journey" data-testid="sanctuary-my-journey-link" style={{ fontFamily: "'Cinzel', Georgia, serif", fontSize: 12, letterSpacing: '0.12em', color: '#8b3a12' }}>
          See your whole journey, book by book →
        </Link>
      </p>

      {/* ── SECTION 2 — BOOK WHISPERS ──────────────────────────────── */}
      {/* Landing card + Whisper-a-Book modal. Now stamps user_id when authed. */}
      <BookWhispersSection activeLang={activeLang} backendUrl={BACKEND_URL} />
    </div>
  );
}
